/**
 * TextBox Component.
 * Its Wrapper Component for Kepler `Input` Component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _toNumber from 'lodash/toNumber';
import FormField from 'kepler/lib/components/FormField';
import Input from 'kepler/lib/components/Input';
import * as shape from '../../constants/PropTypesShapes';
import contentHelper from '../../../lib/contentHelper';

class TextBox extends Component {
  // Set the Field Settings and Error as States.
  constructor(props) {
    super(props);

    this.state = {
      options: props.options,
      errors: [],
    };
  }

  // Every Component Re-render with New Props.
  // This will update the Field Settings and validate according to the prop change during Topic Tree Selection.
  componentWillReceiveProps(props) {
    const { validate, options: { value } } = props;

    this.setState({
      options: props.options,
    });

    if (validate === true) {
      this.validate(value);
    }
  }

  // Send the Field State to Form.
  setFormState() {
    const options = this.state.options;
    this.props.handleValue(options);
  }

  // Check, If Field Value is empty.
  isEmpty(value) {
    return (this.props.options.isRequired && value === '');
  }

  isLimit(value) {
    return (this.props.options.limit && _toNumber(this.props.options.limit) < value.length);
  }

  // Check whether there is required URL need be added in the TextBox
  isRequiredUrl(value) {
    if (!this.props.options || !this.props.options.urlValidation) {
      return false;
    }

    return typeof value !== 'string' || value.indexOf(this.props.options.urlValidation) < 0;
  }

  // Validate Field Value and Update the State.
  validate(value) {
    const errors = [];
    const options = this.props.options;

    if (this.isEmpty(value)) {
      errors.push(this.props.errorWarn.required);
      options.hasError = true;
      options.value = '';
    } else if (this.isLimit(value)) {
      errors.push(`${this.props.errorWarn.limit}: ${_toNumber(this.props.options.limit)}`);
      options.hasError = true;
      options.value = value.slice(0, _toNumber(this.props.options.limit));
    } else if (this.isRequiredUrl(value)) {
      errors.push(contentHelper(this.props.errorWarn.invalidURL, { link: options.urlValidation }));
      options.hasError = true;
      options.value = value;
    } else {
      options.hasError = false;
      options.value = value;
    }

    this.setState({
      options,
      errors,
    });
  }

  // Handle during Blur Event.
  // It is similar to `handleChange` Method.
  // Send value to `validate` then to `form` state.
  handleBlur(value) {
    this.validate(value);
    this.setFormState();
  }

  // Handle during Change Event.
  // Send value to `validate` then to `form` state.
  handleChange(value) {
    this.validate(value);
    this.setFormState();
  }

  // Handle during every Key Press Event.
  // Allow number keys to be entered.
  handleKeyPress(event) {
    const isNumberBox = (this.state.options.type === 'number');
    if (isNumberBox && event.which !== 8 && isNaN(String.fromCharCode(event.which))) {
      event.preventDefault(); // stop character from entering input
    }
  }

  // Render the `TextBox` Component.
  render() {
    return (
      <FormField>
        <label htmlFor={this.props.options.id} className="formLabel">
          {this.props.options.id}
        </label>
        <Input
          onBlur={e => this.handleBlur(e.target.value)}
          onChange={value => this.handleChange(value)}
          onKeyPress={e => this.handleKeyPress(e)}
          type="text"
          placeholder={this.props.options.label}
          id={this.props.options.id}
          name={this.props.options.id}
          value={this.state.options.value}
          errors={this.state.errors}
        />
      </FormField>
    );
  }
}

// TextBox Props Types.
TextBox.propTypes = {
  // Simple Props.
  validate: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types

  // Complex Props.
  options: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    limit: PropTypes.string,
    isRequired: PropTypes.bool,
    value: PropTypes.string,
    hasError: PropTypes.bool,
    urlValidation: PropTypes.string,
  }),

  // Method Props.
  handleValue: PropTypes.func.isRequired,
  errorWarn: PropTypes.shape(shape.WEBFORM_ERRORS),
};

// TextBox Props default values.
TextBox.defaultProps = {
  validate: false,
  options: {},
  errorWarn: {},
};

export default TextBox;
